// ABOUTME: React context tracking the current user's active cart and exposing cart actions.
// ABOUTME: Provides item count, drawer open state, and add/discard/convert/edit operations.

import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { AddToCartOptions, ConvertCartPayload, useCart } from '../../hooks/useCart';
import { useIsCartEnabled } from '../../hooks/useCpqSettings';
import { OfferDetail } from '../../hooks/useOffer';

interface CartContextValue {
    enabled: boolean;
    cart: OfferDetail | null;
    itemCount: number;
    loading: boolean;
    refresh: () => Promise<void>;
    addToCart: (productId: string, quantity?: number, options?: AddToCartOptions) => Promise<void>;
    discard: () => Promise<void>;
    convert: (payload: ConvertCartPayload) => Promise<{ offerId: string; offerNumber: string }>;
    updateItem: (lineItemId: string, payload: { quantity?: number }) => Promise<void>;
    removeItem: (lineItemId: string) => Promise<void>;
    isDrawerOpen: boolean;
    openDrawer: () => void;
    closeDrawer: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

/**
 * Hook to consume the cart context. Returns null when used outside a CartProvider.
 */
export function useCartContext(): CartContextValue | null {
    return useContext(CartContext);
}

export function CartProvider({ children }: { children: ReactNode }) {
    const enabled = useIsCartEnabled();
    const {
        loading,
        getActiveCart,
        addToCart: addToCartApi,
        discardCart,
        convertCart,
        updateCartItem,
        removeCartItem,
    } = useCart();

    const [cart, setCart] = useState<OfferDetail | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const refresh = useCallback(async () => {
        if (!enabled) {
            setCart(null);
            return;
        }
        try {
            const result = await getActiveCart();
            setCart(result ?? null);
        } catch {
            setCart(null);
        }
    }, [enabled, getActiveCart]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const cartId = cart?.offer?.id as string | undefined;

    const addToCart = useCallback(
        async (productId: string, quantity = 1, options?: AddToCartOptions) => {
            await addToCartApi(productId, quantity, options);
            await refresh();
        },
        [addToCartApi, refresh],
    );

    const discard = useCallback(async () => {
        await discardCart();
        await refresh();
    }, [discardCart, refresh]);

    const convert = useCallback(
        async (payload: ConvertCartPayload) => {
            const result = await convertCart(payload);
            await refresh();
            return result;
        },
        [convertCart, refresh],
    );

    const updateItem = useCallback(
        async (lineItemId: string, payload: { quantity?: number }) => {
            if (!cartId) return;
            await updateCartItem(cartId, lineItemId, payload);
            await refresh();
        },
        [cartId, updateCartItem, refresh],
    );

    const removeItem = useCallback(
        async (lineItemId: string) => {
            if (!cartId) return;
            await removeCartItem(cartId, lineItemId);
            await refresh();
        },
        [cartId, removeCartItem, refresh],
    );

    const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
    const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

    const itemCount = cart?.lineItems?.length ?? 0;

    const value = useMemo<CartContextValue>(
        () => ({
            enabled,
            cart,
            itemCount,
            loading,
            refresh,
            addToCart,
            discard,
            convert,
            updateItem,
            removeItem,
            isDrawerOpen,
            openDrawer,
            closeDrawer,
        }),
        [
            enabled,
            cart,
            itemCount,
            loading,
            refresh,
            addToCart,
            discard,
            convert,
            updateItem,
            removeItem,
            isDrawerOpen,
            openDrawer,
            closeDrawer,
        ],
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
